import { REPORT_STEPS } from "../../constants/reportBuilder.js";
import { emptyRuleGroup } from "./queryBuilderConfig.js";
import { formatCalculatedFieldSql, formatRuleGroupSql, ruleGroupHasRules } from "./queryBuilderSql.js";
import {
  buildMetricsOrderBy,
  formatMetricSql,
  metricIsComplete,
  normalizeMetric,
} from "./reportBuilderMetrics.js";

function nextId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
}

export function createFilterRow() {
  return { id: nextId("flt"), field: "", op: "eq", value: "" };
}

export { createMetricRow } from "./reportBuilderMetrics.js";

export function createFieldLogic() {
  return { id: nextId("lgc"), query: emptyRuleGroup(), then: "" };
}

export function createCalculatedField() {
  return { id: nextId("fld"), name: "", logics: [createFieldLogic()], elseExpression: "" };
}

/** Supports legacy `{ query }` shape from earlier builds. */
export function normalizeCalculatedField(field) {
  if (field.logics?.length) return field;
  if (field.query) {
    return {
      ...field,
      logics: [{ id: nextId("lgc"), query: field.query, then: "" }],
      elseExpression: field.elseExpression ?? "",
    };
  }
  return { ...field, logics: [createFieldLogic()], elseExpression: "" };
}

export function calculatedFieldIsComplete(field) {
  const normalized = normalizeCalculatedField(field);
  if (!normalized.name?.trim()) return false;
  return normalized.logics.some((l) => ruleGroupHasRules(l.query));
}

export function getStepIndex(stepId) {
  return REPORT_STEPS.findIndex((s) => s.id === stepId);
}

export function getAdjacentStep(stepId, direction) {
  const idx = getStepIndex(stepId);
  const next = REPORT_STEPS[idx + direction];
  return next?.id ?? null;
}

export function buildReportConfig(state) {
  return {
    datasetId: state.datasetId,
    filterLogic: state.filterLogic ?? "and",
    filters: state.filters.filter((f) => f.field && f.value),
    session: state.session,
    calculatedFields: state.calculatedFields
      .map(normalizeCalculatedField)
      .filter(calculatedFieldIsComplete),
    groupBy: state.groupBy,
    metrics: state.metrics.map(normalizeMetric).filter(metricIsComplete),
    visualization: state.visualization,
  };
}

export function buildQueryPreview(config, dataset) {
  if (!dataset) return "-- Select a dataset";
  const fields = dataset.fields ?? [];
  const calcParts = config.calculatedFields.map((f) => formatCalculatedFieldSql(f)).filter(Boolean);
  const selectParts = [
    ...calcParts,
    ...config.groupBy.map((g) => g),
    ...config.metrics.map((m) => formatMetricSql(m)).filter(Boolean),
  ];
  const select = selectParts.length ? selectParts.join(", ") : fields.slice(0, 4).join(", ");
  const joiner = config.filterLogic === "or" ? "\n  OR " : "\n  AND ";
  const where = config.filters.map((f) => formatFilterClause(f)).join(joiner);
  const group = config.groupBy.length ? `GROUP BY ${config.groupBy.join(", ")}` : "";
  const orderBy = buildMetricsOrderBy(config.metrics);

  const sessionStart = formatRuleGroupSql(config.session?.startQuery);
  const sessionEnd = formatRuleGroupSql(config.session?.endQuery);
  const sessionLines = [];
  if (sessionStart) sessionLines.push(`-- SESSION START WHEN\n-- ${sessionStart}`);
  if (sessionEnd) sessionLines.push(`-- SESSION END WHEN\n-- ${sessionEnd}`);

  return [
    ...sessionLines,
    `SELECT ${select}`,
    `FROM ${dataset.id}_events`,
    where ? `WHERE ${where}` : "",
    group,
    orderBy,
    "LIMIT 1000",
  ]
    .filter(Boolean)
    .join("\n");
}

function formatFilterClause(filter) {
  const ops = { eq: "=", neq: "!=", gt: ">", lt: "<", between: "BETWEEN", contains: "LIKE", in: "IN" };
  const op = ops[filter.op] ?? filter.op;
  if (filter.op === "contains") return `${filter.field} LIKE '%${filter.value}%'`;
  if (filter.op === "between") {
    const [a, b] = filter.value.split(",").map((v) => v.trim());
    return `${filter.field} BETWEEN '${a ?? ""}' AND '${b ?? ""}'`;
  }
  return `${filter.field} ${op} '${filter.value}'`;
}

export function validateStep(stepId, state) {
  switch (stepId) {
    case "dataset":
      return state.datasetId ? null : "Select a dataset";
    case "metrics":
      return state.metrics.some(metricIsComplete) ? null : "Add at least one metric with a field or expression";
    case "save":
      return state.reportName.trim() ? null : "Enter a report name";
    default:
      return null;
  }
}

/** GROUP BY is optional — only recommended when aggregating with dimensions. */
export function isGroupByRecommended(config) {
  return config.metrics.length > 0 && config.groupBy.length === 0;
}

export function groupByHint(config) {
  if (config.metrics.length === 0 && config.groupBy.length === 0) {
    return "Optional — skip for row-level detail reports.";
  }
  if (isGroupByRecommended(config)) {
    return "Recommended — add dimensions so metrics roll up per group instead of one global total.";
  }
  if (config.groupBy.length > 0) {
    return `${config.groupBy.length} dimension${config.groupBy.length === 1 ? "" : "s"} selected.`;
  }
  return "Optional — not required for this report shape.";
}

export function formatRowCount(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
